import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth, formatApiErrorDetail } from '../../auth';
import { AvatarCropper, croppedAvatar } from './AvatarCropper';
import { AvatarActionControls } from './AvatarActionControls';
import { AvatarRemoveDialog } from './AvatarRemoveDialog';

export const AvatarEditor = ({ onSaved, children }) => {
  const { user, updateUser, authHeader } = useAuth();
  const input = useRef(null), actionTrigger = useRef(null), saving = useRef(false);
  const [file, setFile] = useState(null), [preview, setPreview] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState(''), [notice, setNotice] = useState('');
  const [cropArea, setCropArea] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false), [removeError, setRemoveError] = useState('');
  const editDisabled = busy || !!file || removeOpen;
  const requestAction = (event) => {
    if (editDisabled) return;
    setError(''); setNotice('');
    if (user.avatar_id) {
      actionTrigger.current = event.currentTarget;
      setRemoveError(''); setRemoveOpen(true);
    } else input.current?.click();
  };
  useEffect(() => { if (!file) { setPreview(''); return; } const url = URL.createObjectURL(file); setPreview(url); return () => URL.revokeObjectURL(url); }, [file]);
  const choose = (event) => {
    const selected = event.target.files?.[0]; event.target.value = ''; setError(''); setNotice('');
    if (!selected) return;
    if (selected.size > 5 * 1024 * 1024) { setError('Choose an image smaller than 5 MB.'); return; }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(selected.type)) { setError('Choose a JPG, PNG or WebP image.'); return; }
    setCropArea(null); setFile(selected);
  };
  const save = async (remove = false) => {
    if (saving.current) return;
    saving.current = true;
    setBusy(true); setError(''); setRemoveError(''); setNotice('');
    try {
      const form = new FormData();
      if (!remove) {
        if (!cropArea || !preview) throw new Error('Wait for the picture preview to load.');
        form.append('file', await croppedAvatar(preview, cropArea), 'profile.webp');
      }
      const { data } = await axios({ method: remove ? 'delete' : 'post', url: `${process.env.REACT_APP_BACKEND_URL}/api/account/avatar`, data: remove ? undefined : form, headers: authHeader() });
      updateUser({ avatar_id: data.avatar_id }); onSaved?.(data.avatar_id); setFile(null); setNotice(remove ? 'Profile picture removed.' : 'Profile picture updated.');
      if (remove) setRemoveOpen(false);
    } catch (failure) { (remove ? setRemoveError : setError)(formatApiErrorDetail(failure.response?.data?.detail || failure.message || 'Unable to update profile picture.')); }
    finally { saving.current = false; setBusy(false); }
  };
  return <div className="avatar-editor" data-testid="avatar-editor">
    <div className="avatar-editor-main">
      <AvatarActionControls user={user} disabled={editDisabled} busy={busy} onAction={requestAction} />
      {children}
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" hidden data-testid="avatar-file-input" aria-label="Choose profile picture" disabled={editDisabled} onChange={choose} />
    </div>
    {preview && <AvatarCropper key={preview} source={preview} disabled={busy} onCrop={setCropArea} onError={(message) => { setError(message); setFile(null); setCropArea(null); }} />}
    {file && <div className="avatar-editor-actions"><Button type="button" data-testid="avatar-save" disabled={busy || !cropArea} onClick={() => save()}><Upload size={16} />{busy ? 'Uploading…' : 'Save picture'}</Button><Button type="button" variant="ghost" data-testid="avatar-cancel" disabled={busy} onClick={() => setFile(null)}>Cancel</Button></div>}
    {error && <p role="alert" className="settings-error" data-testid="avatar-error">{error}</p>}{notice && <p role="status" className="settings-success" data-testid="avatar-success">{notice}</p>}
    <AvatarRemoveDialog open={removeOpen} busy={busy} error={removeError} onOpenChange={(next) => { setRemoveOpen(next); if (!next) setRemoveError(''); }} onConfirm={() => save(true)} onReturnFocus={() => actionTrigger.current?.focus()} />
  </div>;
};